import React from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Paper,
    Container,
    Stack,
    CircularProgress,
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    AdminPanelSettings as AdminIcon,
    MenuBook as MenuBookIcon,
    Login as LoginIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useSetup } from '../context/SetupContext';
import { useJednotkaQuery } from '../api/JsdhNotifikaceService/service/AdminMutations';
import MobileAppSection from '../components/MobileAppSection';
import AppearanceMenu from '../components/AppearanceMenu';

const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const { setupRequired, isLoading: isSetupLoading } = useSetup();
    const { data: jednotka } = useJednotkaQuery();

    // Show loading while checking setup status
    if (isSetupLoading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
            >
                <CircularProgress />
                <Typography variant="body1" sx={{ mt: 2 }}>
                    Načítám...
                </Typography>
            </Box>
        );
    }

    // Redirect to setup wizard if setup is required
    if (setupRequired) {
        return <Navigate to="/setup" replace />;
    }

    const menuItems = [
        {
            label: 'Poplachový displej',
            description: 'Zobrazení aktivních poplachů pro Raspberry Pi',
            icon: <DashboardIcon sx={{ fontSize: 40 }} />,
            path: '/poplach',
            color: 'error.main',
        },
        {
            label: 'Administrace',
            description: 'Správa událostí, uživatelů a nastavení',
            icon: <AdminIcon sx={{ fontSize: 40 }} />,
            path: '/admin',
            color: 'primary.main',
        },
        {
            label: 'Návody',
            description: 'Dokumentace, návody a časté otázky',
            icon: <MenuBookIcon sx={{ fontSize: 40 }} />,
            path: '/dokumentace',
            color: 'info.main',
        },
    ];

    return (
        <Container maxWidth="sm">
            {/* Theme toggle in top-right corner */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 16,
                    right: 16,
                    zIndex: 1000,
                }}
            >
                <AppearanceMenu />
            </Box>

            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="100vh"
                py={4}
            >
                <Typography
                    variant="h4"
                    component="h1"
                    gutterBottom
                    sx={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        mb: 4,
                    }}
                >
                    {jednotka?.nazev || 'Hasiči Online'}
                </Typography>

                <Stack spacing={2} sx={{ width: '100%' }}>
                    {menuItems.map((item) => (
                        <Paper
                            key={item.path}
                            elevation={2}
                            sx={{
                                p: 3,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    elevation: 4,
                                    transform: 'translateY(-2px)',
                                    boxShadow: 4,
                                },
                            }}
                            onClick={() => navigate(item.path)}
                        >
                            <Box display="flex" alignItems="center" gap={2}>
                                <Box
                                    sx={{
                                        color: item.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {item.icon}
                                </Box>
                                <Box>
                                    <Typography variant="h6" component="h2">
                                        {item.label}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {item.description}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    ))}
                </Stack>

                <MobileAppSection />

                {!isAuthenticated && (
                    <Button
                        variant="outlined"
                        startIcon={<LoginIcon />}
                        onClick={() => navigate('/login')}
                        sx={{ mt: 4 }}
                    >
                        Přihlášení
                    </Button>
                )}
            </Box>
        </Container>
    );
};

export default React.memo(LandingPage);
